import React, { useRef, useState, useCallback } from "react";
import "./style.css";
import { Heart } from "lucide-react";
import { ReactionType } from "../../contexts/ReactionsContext";
import BallBurst from "../ball-burst/BallBurst";

interface Props {
    reactions: ReactionType[];
    userReaction: string | null;
    totalLikes: number;
    onOpenLikes?: () => void;
    onReact: (slug: string) => void;
}

const isMobileDevice = () =>
    typeof window !== "undefined" && window.innerWidth <= 850;

const HOVER_DELAY = 600;

const ReactionPicker: React.FC<Props> = ({
    reactions,
    userReaction,
    totalLikes,
    onOpenLikes,
    onReact,
}) => {
    const [open, setOpen] = useState(false);
    const [popping, setPopping] = useState(false);
    const [ballBurst, setBallBurst] = useState(false);
    const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const openTimer  = useRef<ReturnType<typeof setTimeout> | null>(null);
    const holdTimer  = useRef<ReturnType<typeof setTimeout> | null>(null);
    const touchStartTime = useRef(0);
    const touchMoved     = useRef(false);
    const btnRef         = useRef<HTMLButtonElement>(null);

    /* ── timers ── */
    const scheduleOpen = () => {
        cancelClose();
        if (openTimer.current) return;
        openTimer.current = setTimeout(() => { setOpen(true); openTimer.current = null; }, HOVER_DELAY);
    };
    const cancelOpen = () => {
        if (openTimer.current) { clearTimeout(openTimer.current); openTimer.current = null; }
    };
    const scheduleClose = () => {
        cancelOpen();
        closeTimer.current = setTimeout(() => setOpen(false), 200);
    };
    const cancelClose = () => {
        if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    };

    const triggerPop = () => {
        setPopping(false);
        requestAnimationFrame(() => requestAnimationFrame(() => setPopping(true)));
        setTimeout(() => setPopping(false), 320);
    };

    const handleReact = useCallback(
        (slug: string) => {
            setOpen(false);
            cancelOpen();
            triggerPop();
            if (slug === "ball") setBallBurst(true);
            onReact(slug);
        },
        [onReact]
    );

    /* ── ícone principal ── */
    const renderMainIcon = () => {
        if (userReaction && userReaction !== "like") {
            if (userReaction === "ball") {
                return <img src="/img/reacts/ball.png" className={`ball-react ${popping ? "pop" : ""}`} alt="" />;
            }
            const found = reactions.find(r => r.slug === userReaction);
            if (found?.emoji) {
                return <span className={`reaction-icon ${popping ? "pop" : ""}`}>{found.emoji}</span>;
            }
        }
        const isLiked = userReaction === "like";
        return (
            <Heart
                className={`reaction-icon icon-post ${popping ? "pop" : ""}`}
                fill={isLiked ? "#e0245e" : "none"}
                color={isLiked ? "#e0245e" : "currentColor"}
                size={20}
            />
        );
    };

    /* ── opção no picker ── */
    const renderOption = (r: ReactionType) => {
        if (r.slug === "like") {
            return <Heart fill="#e0245e" color="#e0245e" size={26} />;
        }
        if (r.slug === "ball") {
            return <img src="/img/reacts/ball.png" className="ball-react-option" alt="" />;
        }
        return <span className="opt-emoji">{r.emoji ?? "⚽"}</span>;
    };

    /* ── DESKTOP ── */
    const handleMouseEnter = () => { if (!isMobileDevice()) { cancelClose(); scheduleOpen(); } };
    const handleMouseLeave = () => { if (!isMobileDevice()) { cancelOpen(); scheduleClose(); } };
    const handleClick = () => {
        if (isMobileDevice()) return;
        const defaultSlug = reactions[0]?.slug ?? "like";
        handleReact(defaultSlug);
    };

    /* ── MOBILE ── */
    const handleTouchStart = (e: React.TouchEvent) => {
        touchMoved.current = false;
        touchStartTime.current = Date.now();
        holdTimer.current = setTimeout(() => setOpen(prev => !prev), 450);
    };
    const handleTouchMove = () => {
        touchMoved.current = true;
        if (holdTimer.current) clearTimeout(holdTimer.current);
    };
    const handleTouchEnd = () => {
        if (holdTimer.current) clearTimeout(holdTimer.current);
        if (touchMoved.current) return;
        const elapsed = Date.now() - touchStartTime.current;
        if (elapsed < 450 && !open) {
            handleReact(reactions[0]?.slug ?? "like");
        }
    };

    /* ── label da reação ativa ── */
    const activeLabel = userReaction
        ? reactions.find(r => r.slug === userReaction)?.label ?? "Curtir"
        : null;

    const reactionClass = userReaction ? `reacted reacted-${userReaction}` : "";

    return (
        <>
            <BallBurst
                active={ballBurst}
                onDone={() => setBallBurst(false)}
                anchorRef={btnRef as React.RefObject<HTMLElement>}
            />

            <div
                className="reaction-picker-wrapper"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {/* ── Picker flutuante ── */}
                {open && reactions.length > 0 && (
                    <div
                        className="reaction-picker"
                        onMouseEnter={cancelClose}
                        onMouseLeave={scheduleClose}
                    >
                        {reactions.map((r) => (
                            <button
                                key={r.slug}
                                title={r.label}
                                className={`reaction-option${userReaction === r.slug ? " active-reaction" : ""}`}
                                onMouseDown={(e) => { e.preventDefault(); handleReact(r.slug); }}
                                onTouchEnd={(e)  => { e.preventDefault(); handleReact(r.slug); }}
                            >
                                {renderOption(r)}
                            </button>
                        ))}
                    </div>
                )}

                {/* ── Botão principal: curtir direto ── */}
                <button
                    ref={btnRef}
                    className={`reaction-btn ${reactionClass}`}
                    onClick={handleClick}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    aria-label={activeLabel ?? "Curtir"}
                >
                    {renderMainIcon()}
                </button>
            </div>

            {/* ── Contador clicável ──
            {totalLikes > 0 && (
                <span
                    className="reaction-total"
                    onClick={() => onOpenLikes?.()}
                    style={{ cursor: onOpenLikes ? "pointer" : "default" }}
                >
                    {totalLikes}
                </span>
            )} */}
        </>
    );
};

export default ReactionPicker;